
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  AlertTriangle, ArrowLeft, CheckCircle2, XCircle, Clock, Filter, Trash2, Wrench, RefreshCw
} from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import api from '@/lib/api';
import { toast } from 'sonner';

const CRITICALITY_STYLES = {
  high: 'bg-red-500/15 text-red-400 border-red-500/30',
  medium: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  low: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30',
};

const SOURCE_LABELS = {
  payroll_advance: 'Payroll Advance',
  send_invoice: 'Send Invoice',
  invoice_payment: 'Invoice Payment',
  convert_to_project: 'Convert to Project',
  approve_lead: 'Approve Lead',
  lead_next_step: 'Lead Next Step',
  email_draft_create: 'Email Draft',
  email_draft_approve: 'Email Draft Approve',
  advance_proposal: 'Advance Proposal',
  close_proposal: 'Close Proposal',
};

function formatTimestamp(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

export default function SystemLogsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [errors, setErrors] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [critFilter, setCritFilter] = useState('all');
  const [showDismissed, setShowDismissed] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (sourceFilter !== 'all') params.append('source', sourceFilter);
      if (critFilter !== 'all') params.append('criticality', critFilter);
      if (!showDismissed) params.append('dismissed', 'false');
      const [errRes, sumRes] = await Promise.all([
        api.get(`/system-errors?${params.toString()}`),
        api.get('/system-errors/summary'),
      ]);
      setErrors(errRes.data || []);
      setSummary(sumRes.data || {});
    } catch {
      toast.error('Failed to load system logs');
    } finally {
      setLoading(false);
    }
  }, [sourceFilter, critFilter, showDismissed]);

  useEffect(() => {
    if (user?.role !== 'Administrator') {
      navigate('/');
      return;
    }
    fetchData();
    fetchMaintenanceRuns();
  }, [fetchData, navigate, user?.role]);

  const [maintenanceRuns, setMaintenanceRuns] = useState({});
  const fetchMaintenanceRuns = async () => {
    try {
      const r = await api.get('/admin/maintenance-runs');
      setMaintenanceRuns(r.data?.runs || {});
    } catch { /* non-blocking */ }
  };
  const formatRun = (key) => {
    const run = maintenanceRuns[key];
    if (!run?.last_run_at) return null;
    const d = new Date(run.last_run_at);
    if (isNaN(d.getTime())) return null;
    const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const timeStr = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    return run.last_run_by_name
      ? `Last run ${dateStr} ${timeStr} by ${run.last_run_by_name}`
      : `Last run ${dateStr} ${timeStr}`;
  };

  const dismissError = async (id) => {
    try {
      await api.post(`/system-errors/${id}/dismiss`);
      setErrors(prev => prev.filter(e => e.id !== id));
      setSummary(prev => ({ ...prev, active: Math.max(0, (prev.active || 0) - 1) }));
      toast.success('Dismissed');
    } catch {
      toast.error('Failed to dismiss');
    }
  };

  const dismissAll = async () => {
    try {
      const r = await api.post('/system-errors/dismiss-all');
      toast.success(`Dismissed ${r.data.dismissed_count} errors`);
      fetchData();
    } catch {
      toast.error('Failed to dismiss all');
    }
  };

  const [normalizing, setNormalizing] = useState(false);
  const normalizeReimbursements = async () => {
    if (normalizing) return;
    if (!window.confirm('Normalize reimbursement line items across ALL invoices? This is a safe, idempotent, one-time data sweep that heals historic records. Proceed?')) return;
    setNormalizing(true);
    try {
      const r = await api.post('/invoices/admin/normalize-reimbursements');
      const d = r.data;
      toast.success(`Normalized ${d.healed} invoice${d.healed === 1 ? '' : 's'} (${d.skipped_already_clean} already clean, ${d.scanned} scanned)`);
      await fetchMaintenanceRuns();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Normalize failed');
    } finally {
      setNormalizing(false);
    }
  };

  const [normalizingState, setNormalizingState] = useState(false);
  const normalizeInvoiceState = async () => {
    if (normalizingState) return;
    if (!window.confirm('Recompute status / amount_paid / balance_due / discount_total on every invoice to match the actual payments + discounts. Idempotent. Proceed?')) return;
    setNormalizingState(true);
    try {
      const r = await api.post('/invoices/admin/normalize-state');
      const d = r.data;
      if (d.healed === 0) {
        toast.success(`All ${d.scanned} invoices already consistent`);
      } else {
        const changes = d.details.map(x => `${x.invoice_number}: ${Object.keys(x.changes).join(', ')}`).join('; ');
        toast.success(`Healed ${d.healed} of ${d.scanned} invoices`, { description: changes.length > 160 ? changes.slice(0, 157) + '...' : changes });
      }
      await fetchMaintenanceRuns();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Normalize failed');
    } finally {
      setNormalizingState(false);
    }
  };

  const [normalizingJeTypes, setNormalizingJeTypes] = useState(false);
  const normalizeJeEventTypes = async () => {
    if (normalizingJeTypes) return;
    if (!window.confirm('Backfill event_type on historic calendar journal entries so they show human-readable source labels (Vendor Payment, Rent Due, Recurring Payment, etc.) instead of the internal "acct_calendar" tag. Idempotent. Proceed?')) return;
    setNormalizingJeTypes(true);
    try {
      const r = await api.post('/accounting/admin/normalize-je-event-types');
      const d = r.data;
      toast.success(`Updated ${d.updated} of ${d.scanned} scanned, skipped ${d.skipped}`);
      await fetchMaintenanceRuns();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Normalize failed');
    } finally {
      setNormalizingJeTypes(false);
    }
  };

  const [pushRefreshOpen, setPushRefreshOpen] = useState(false);
  const [pushRefreshNote, setPushRefreshNote] = useState('');
  const [pushingRefresh, setPushingRefresh] = useState(false);

  const [strippingHtml, setStrippingHtml] = useState(false);
  const stripReimbHtml = async () => {
    if (strippingHtml) return;
    if (!window.confirm('Strip HTML markup from reimbursement descriptions on every invoice + reimbursement record. Idempotent; already-clean entries are skipped. Proceed?')) return;
    setStrippingHtml(true);
    try {
      const r = await api.post('/invoices/admin/strip-reimb-html');
      const d = r.data;
      toast.success(`Healed ${d.invoices_healed} invoice${d.invoices_healed === 1 ? '' : 's'} + ${d.reimbursements_healed} reimbursement${d.reimbursements_healed === 1 ? '' : 's'} (${d.invoices_scanned + d.reimbursements_scanned} records scanned)`);
      await fetchMaintenanceRuns();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sweep failed');
    } finally { setStrippingHtml(false); }
  };
  const pushRefreshAll = async () => {
    if (pushingRefresh) return;
    if (!window.confirm('Every active user will see a non-dismissible banner counting down to a forced page reload. Any unsaved work in the user\'s current form will be lost. Proceed?')) return;
    setPushingRefresh(true);
    try {
      const r = await api.post('/admin/push-refresh', { note: pushRefreshNote });
      toast.success(`Refresh push sent. Active users will see the banner within ~60s. Pushed at ${new Date(r.data.force_refresh_at).toLocaleTimeString()}.`);
      setPushRefreshOpen(false);
      setPushRefreshNote('');
      // Bump our own baseline so we don't show the banner to ourselves.
      try { sessionStorage.setItem('forceRefreshBaselineAt', r.data.force_refresh_at); } catch { /* ignore */ }
      // Manually upsert into runs map so the timestamp shows immediately.
      setMaintenanceRuns(prev => ({
        ...prev,
        push_refresh: {
          key: 'push_refresh',
          last_run_at: r.data.force_refresh_at,
          last_run_by_name: r.data.pushed_by_name || '',
          last_result: { note: pushRefreshNote },
        },
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Push failed');
    } finally { setPushingRefresh(false); }
  };

  const [backfillingNotes, setBackfillingNotes] = useState(false);
  const backfillMeetingNoteStatusLog = async () => {
    if (backfillingNotes) return;
    if (!window.confirm('Walk every existing meeting note and create the matching project status log entries (one per linked project). Idempotent; already-synced notes are skipped, so this is safe to re-run. Proceed?')) return;
    setBackfillingNotes(true);
    try {
      const r = await api.post('/projects/admin/backfill-meeting-note-status-log');
      const d = r.data;
      if (d.created === 0) {
        toast.success(`All ${d.scanned} meeting notes already had status log entries (${d.skipped} skipped, no project linked)`);
      } else {
        toast.success(`Created ${d.created} new status log entr${d.created === 1 ? 'y' : 'ies'} from ${d.scanned} meeting notes (${d.skipped} skipped)`);
      }
      await fetchMaintenanceRuns();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backfill failed');
    } finally {
      setBackfillingNotes(false);
    }
  };

  const [migratingPayroll, setMigratingPayroll] = useState(false);
  const migratePayrollToSundayWeek = async () => {
    if (migratingPayroll) return;
    if (!window.confirm('Shift every payroll period from Mon–Sun to Sun–Sat (period start/end shift back 1 day). Also re-buckets reimbursements and recomputes timesheet week_start. Idempotent: already-Sunday periods are skipped. This is a ONE-TIME structural migration. Proceed?')) return;
    setMigratingPayroll(true);
    try {
      const r = await api.post('/payroll-periods/admin/migrate-to-sunday-week');
      const d = r.data;
      toast.success(`Migrated · ${d.shifted_periods} periods shifted, ${d.shifted_reimbursements} reimbursements re-bucketed, ${d.updated_timesheets} timesheet week_starts recomputed (${d.skipped_periods} periods skipped)`);
      await fetchMaintenanceRuns();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Migration failed');
    } finally {
      setMigratingPayroll(false);
    }
  };

  const [resetProposalOpen, setResetProposalOpen] = useState(false);
  const [nextProposalNumber, setNextProposalNumber] = useState('');
  const [resettingProposal, setResettingProposal] = useState(false);
  const resetProposalCounter = async () => {
    const raw = (nextProposalNumber || '').trim();
    if (!raw) return toast.error('Enter a number (e.g. 38 or NYCD 26-038)');
    if (!window.confirm(`Reset the proposal counter so the NEXT new proposal is numbered ${raw}? Existing records are untouched. If a proposal already exists with that number, the next create will throw a duplicate error - clean those up first.`)) return;
    setResettingProposal(true);
    try {
      const r = await api.post('/proposals/admin/set-next-number', { next_number: raw });
      toast.success(`Next proposal will be ${r.data.next_proposal_number}`);
      setResetProposalOpen(false);
      setNextProposalNumber('');
      await fetchMaintenanceRuns();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    } finally {
      setResettingProposal(false);
    }
  };

  if (user?.role !== 'Administrator') return null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="system-logs-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/')} data-testid="system-logs-back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-[22px] font-medium tracking-tight">System Logs</h1>
          </div>
        </div>
        {(summary.active || 0) > 0 && (
          <Button variant="outline" size="sm" onClick={dismissAll} data-testid="dismiss-all-btn">
            <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Dismiss All
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="system-logs-summary">
        <Card className="border-border bg-card shadow-none">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground">Active Errors</p>
              <p className="text-2xl font-heading font-bold mt-1">{summary.active || 0}</p>
            </div>
            <div className="h-10 w-10 rounded-md flex items-center justify-center bg-amber-500/15">
              <AlertTriangle className="h-5 w-5 text-amber-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card shadow-none">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground">High Priority</p>
              <p className="text-2xl font-heading font-bold mt-1">{summary.high || 0}</p>
            </div>
            <div className="h-10 w-10 rounded-md flex items-center justify-center bg-red-500/15">
              <XCircle className="h-5 w-5 text-red-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card shadow-none">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground">Medium</p>
              <p className="text-2xl font-heading font-bold mt-1">{summary.medium || 0}</p>
            </div>
            <div className="h-10 w-10 rounded-md flex items-center justify-center bg-amber-500/15">
              <Clock className="h-5 w-5 text-amber-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card shadow-none">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground">Total Logged</p>
              <p className="text-2xl font-heading font-bold mt-1">{summary.total || 0}</p>
            </div>
            <div className="h-10 w-10 rounded-md flex items-center justify-center bg-zinc-500/15">
              <CheckCircle2 className="h-5 w-5 text-zinc-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Push Refresh - broadcasts a reload prompt to every active user. Useful right after deploying. */}
      <Card className="border-amber-500/30 bg-amber-500/5 shadow-none" data-testid="push-refresh-card">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-md flex items-center justify-center bg-amber-500/15 shrink-0">
                <RefreshCw className="h-5 w-5 text-amber-400" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-amber-400">Broadcast</p>
                <p className="text-sm font-semibold mt-0.5">Push refresh to all users</p>
                <p className="text-xs text-muted-foreground mt-1 max-w-xl">
                  Bumps a global timestamp every active client polls. Users see a non-dismissible
                  banner with a 30-second countdown and a &quot;Refresh Now&quot; button (auto-reloads after).
                  Detected within ~60 seconds of pushing. Most useful right after deploying new code.
                </p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPushRefreshOpen(true)}
                className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300"
                data-testid="push-refresh-btn"
              >
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                Push Refresh…
              </Button>
              {formatRun('push_refresh') && (
                <p className="text-xs text-muted-foreground" data-testid="last-run-push_refresh">{formatRun('push_refresh')}</p>
              )}
            </div>
          </div>
          {pushRefreshOpen && (
            <div className="mt-3 pt-3 border-t border-amber-500/20 flex items-end gap-2 flex-wrap" data-testid="push-refresh-form">
              <div className="flex-1 min-w-[220px]">
                <label className="text-xs text-muted-foreground">Optional note shown in the banner</label>
                <input
                  type="text"
                  value={pushRefreshNote}
                  onChange={e => setPushRefreshNote(e.target.value)}
                  placeholder='e.g. "Deployed new invoice features. Please refresh."'
                  className="mt-1 w-full bg-background border border-border rounded h-9 px-3 text-sm"
                  maxLength={280}
                  data-testid="push-refresh-note"
                />
              </div>
              <Button size="sm" onClick={pushRefreshAll} disabled={pushingRefresh} className="bg-amber-500 hover:bg-amber-400 text-zinc-900" data-testid="push-refresh-confirm">
                {pushingRefresh ? 'Pushing…' : 'Push Now'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setPushRefreshOpen(false); setPushRefreshNote(''); }} data-testid="push-refresh-cancel">
                Cancel
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Maintenance Tools - grouped into an accordion to keep the page tidy. All sweeps are idempotent. */}
      <Accordion type="multiple" className="w-full space-y-2" data-testid="maintenance-accordion">

        <AccordionItem value="invoices-accounting" className="border border-border rounded-md bg-card" data-testid="maintenance-group-invoices">
          <AccordionTrigger className="px-4 py-3 hover:no-underline">
            <div className="flex items-center gap-3">
              <Wrench className="h-4 w-4 text-teal-400" />
              <div className="text-left">
                <p className="text-sm font-semibold">Invoices &amp; Accounting</p>
                <p className="text-xs text-muted-foreground">4 sweeps · normalize legacy reimbursement records, reconcile invoice status/balances, backfill JE source labels, strip HTML from reimbursement descriptions</p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-2 pb-3 space-y-3">
      <Card className="border-border bg-card shadow-none" data-testid="maintenance-tools-card">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-md flex items-center justify-center bg-teal-500/15 shrink-0">
                <Wrench className="h-5 w-5 text-teal-400" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground">Maintenance</p>
                <p className="text-sm font-semibold mt-0.5">Normalize invoice reimbursement records</p>
                <p className="text-xs text-muted-foreground mt-1 max-w-xl">
                  One-time, idempotent sweep that migrates historic reimbursement line items
                  (bare string IDs or dicts missing <code className="text-xs px-1 py-0.5 rounded bg-muted">id</code>)
                  to the current schema so payment logging works across all invoices.
                </p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={normalizeReimbursements}
                disabled={normalizing}
                data-testid="normalize-reimbursements-btn"
              >
                <Wrench className="h-3.5 w-3.5 mr-1.5" />
                {normalizing ? 'Normalizing…' : 'Run Normalize'}
              </Button>
              {formatRun('invoice_normalize_reimbursements') && (
                <p className="text-xs text-muted-foreground" data-testid="last-run-invoice_normalize_reimbursements">{formatRun('invoice_normalize_reimbursements')}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invoice state consistency sweep */}
      <Card className="border-border bg-card shadow-none" data-testid="invoice-state-sweep-card">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-md flex items-center justify-center bg-teal-500/15 shrink-0">
                <Wrench className="h-5 w-5 text-teal-400" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground">Maintenance</p>
                <p className="text-sm font-semibold mt-0.5">Reconcile invoice status &amp; balances</p>
                <p className="text-xs text-muted-foreground mt-1 max-w-xl">
                  Recomputes <code className="text-xs px-1 py-0.5 rounded bg-muted">status</code>, <code className="text-xs px-1 py-0.5 rounded bg-muted">amount_paid</code>, <code className="text-xs px-1 py-0.5 rounded bg-muted">balance_due</code>, and <code className="text-xs px-1 py-0.5 rounded bg-muted">discount_total</code> on every invoice to match its actual payments and discounts.
                  Safe to run any time. Fixes drifts where the list and detail panel disagree.
                </p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={normalizeInvoiceState}
                disabled={normalizingState}
                data-testid="normalize-invoice-state-btn"
              >
                <Wrench className="h-3.5 w-3.5 mr-1.5" />
                {normalizingState ? 'Reconciling…' : 'Reconcile Now'}
              </Button>
              {formatRun('invoice_normalize_state') && (
                <p className="text-xs text-muted-foreground" data-testid="last-run-invoice_normalize_state">{formatRun('invoice_normalize_state')}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Journal entry event_type backfill */}
      <Card className="border-border bg-card shadow-none" data-testid="je-event-type-sweep-card">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-md flex items-center justify-center bg-teal-500/15 shrink-0">
                <Wrench className="h-5 w-5 text-teal-400" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground">Maintenance</p>
                <p className="text-sm font-semibold mt-0.5">Backfill journal entry source labels</p>
                <p className="text-xs text-muted-foreground mt-1 max-w-xl">
                  Adds <code className="text-xs px-1 py-0.5 rounded bg-muted">event_type</code> to historic
                  calendar-sourced journal entries so the Source column shows human-readable labels
                  (Vendor Payment Due, Rent Due Date, Recurring Payment, etc.) instead of
                  <code className="text-xs px-1 py-0.5 rounded bg-muted ml-1">acct_calendar</code>. Idempotent.
                </p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={normalizeJeEventTypes}
                disabled={normalizingJeTypes}
                data-testid="normalize-je-event-types-btn"
              >
                <Wrench className="h-3.5 w-3.5 mr-1.5" />
                {normalizingJeTypes ? 'Backfilling…' : 'Run Backfill'}
              </Button>
              {formatRun('accounting_normalize_je_event_types') && (
                <p className="text-xs text-muted-foreground" data-testid="last-run-accounting_normalize_je_event_types">{formatRun('accounting_normalize_je_event_types')}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Strip HTML from reimbursement descriptions */}
      <Card className="border-border bg-card shadow-none" data-testid="strip-reimb-html-card">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-md flex items-center justify-center bg-teal-500/15 shrink-0">
                <Wrench className="h-5 w-5 text-teal-400" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground">Maintenance</p>
                <p className="text-sm font-semibold mt-0.5">Strip HTML from reimbursement descriptions</p>
                <p className="text-xs text-muted-foreground mt-1 max-w-xl">
                  Reimbursement descriptions are authored via a rich-text editor, so historical
                  records may have stored HTML markup. This sweep replaces those with plain text on
                  every invoice line item AND on the source reimbursement record. Idempotent.
                </p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={stripReimbHtml}
                disabled={strippingHtml}
                data-testid="strip-reimb-html-btn"
              >
                <Wrench className="h-3.5 w-3.5 mr-1.5" />
                {strippingHtml ? 'Stripping…' : 'Run Cleanup'}
              </Button>
              {formatRun('invoice_strip_reimb_html') && (
                <p className="text-xs text-muted-foreground" data-testid="last-run-invoice_strip_reimb_html">{formatRun('invoice_strip_reimb_html')}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="proposals-numbering" className="border border-border rounded-md bg-card" data-testid="maintenance-group-proposals">
          <AccordionTrigger className="px-4 py-3 hover:no-underline">
            <div className="flex items-center gap-3">
              <Wrench className="h-4 w-4 text-indigo-400" />
              <div className="text-left">
                <p className="text-sm font-semibold">Proposals &amp; Numbering</p>
                <p className="text-xs text-muted-foreground">1 admin tool · set the next NYCD YY-NNN proposal number</p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-2 pb-3 space-y-3">

      {/* Reset proposal counter */}
      <Card className="border-border bg-card shadow-none" data-testid="reset-proposal-counter-card">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-md flex items-center justify-center bg-indigo-500/15 shrink-0">
                <Wrench className="h-5 w-5 text-indigo-400" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground">Maintenance</p>
                <p className="text-sm font-semibold mt-0.5">Reset next Proposal number</p>
                <p className="text-xs text-muted-foreground mt-1 max-w-xl">
                  Adjusts the auto-increment so the next new Proposal (created from a Lead or directly)
                  starts at the number you choose. Existing proposals are untouched. Useful when migrating
                  to a new numbering policy or after pruning test data.
                </p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setResetProposalOpen(true)}
                data-testid="reset-proposal-counter-btn"
              >
                <Wrench className="h-3.5 w-3.5 mr-1.5" />
                Set Next Number…
              </Button>
              {formatRun('proposal_set_next_number') && (
                <p className="text-xs text-muted-foreground" data-testid="last-run-proposal_set_next_number">{formatRun('proposal_set_next_number')}</p>
              )}
            </div>
          </div>
          {resetProposalOpen && (
            <div className="mt-3 pt-3 border-t border-border flex items-end gap-2 flex-wrap" data-testid="reset-proposal-counter-form">
              <div className="flex-1 min-w-[220px]">
                <label className="text-xs text-muted-foreground">Next Proposal Number</label>
                <input
                  type="text"
                  value={nextProposalNumber}
                  onChange={e => setNextProposalNumber(e.target.value)}
                  placeholder="e.g. 38 or NYCD 26-038"
                  className="mt-1 w-full bg-background border border-border rounded h-9 px-3 text-sm"
                  data-testid="reset-proposal-counter-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Enter just the number (38) or the full ID (NYCD 26-038). The current calendar year is used automatically.
                </p>
              </div>
              <Button size="sm" onClick={resetProposalCounter} disabled={resettingProposal} data-testid="reset-proposal-counter-confirm">
                {resettingProposal ? 'Setting…' : 'Confirm'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setResetProposalOpen(false); setNextProposalNumber(''); }} data-testid="reset-proposal-counter-cancel">
                Cancel
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="meeting-notes" className="border border-border rounded-md bg-card" data-testid="maintenance-group-meetings">
          <AccordionTrigger className="px-4 py-3 hover:no-underline">
            <div className="flex items-center gap-3">
              <Wrench className="h-4 w-4 text-emerald-400" />
              <div className="text-left">
                <p className="text-sm font-semibold">Meeting Notes &amp; Status</p>
                <p className="text-xs text-muted-foreground">1 sweep · backfill missing project status log entries from historical meeting notes</p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-2 pb-3 space-y-3">

      {/* Backfill meeting-note status log entries */}
      <Card className="border-border bg-card shadow-none" data-testid="backfill-meeting-notes-card">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-md flex items-center justify-center bg-emerald-500/15 shrink-0">
                <Wrench className="h-5 w-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground">Maintenance</p>
                <p className="text-sm font-semibold mt-0.5">Backfill Meeting Note Status Log entries</p>
                <p className="text-xs text-muted-foreground mt-1 max-w-xl">
                  One-time sweep that walks every historical meeting note and creates the matching
                  project status log entry (one per linked project, authored by the original PM).
                  Idempotent - already-synced notes are skipped, so it&apos;s safe to re-run.
                </p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={backfillMeetingNoteStatusLog}
                disabled={backfillingNotes}
                data-testid="backfill-meeting-notes-btn"
              >
                <Wrench className="h-3.5 w-3.5 mr-1.5" />
                {backfillingNotes ? 'Backfilling…' : 'Run Backfill'}
              </Button>
              {formatRun('meeting_note_status_log_backfill') && (
                <p className="text-xs text-muted-foreground" data-testid="last-run-meeting_note_status_log_backfill">{formatRun('meeting_note_status_log_backfill')}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
          </AccordionContent>
        </AccordionItem>

        {/* Payroll Sun-Sat migration */}
        <AccordionItem value="payroll-week-migration" className="border border-border rounded-md bg-card" data-testid="maintenance-group-payroll-week">
          <AccordionTrigger className="px-4 py-3 hover:no-underline">
            <div className="flex items-center gap-3">
              <Wrench className="h-4 w-4 text-amber-400" />
              <div className="text-left">
                <p className="text-sm font-semibold">Payroll Week Migration</p>
                <p className="text-xs text-muted-foreground">1 sweep · shift payroll periods from Mon–Sun to Sun–Sat and re-bucket linked data</p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-2 pb-3 space-y-3">
            <Card className="border-border bg-card shadow-none" data-testid="payroll-sun-migration-card">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-md flex items-center justify-center bg-amber-500/15 shrink-0">
                      <Wrench className="h-5 w-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground">One-Time Structural Migration</p>
                      <p className="text-sm font-semibold mt-0.5">Shift Pay Periods Mon–Sun → Sun–Sat</p>
                      <p className="text-xs text-muted-foreground mt-1 max-w-xl">
                        Shifts every payroll period&apos;s start &amp; end back by 1 day so the work-week
                        aligns to Sunday → Saturday. Re-buckets reimbursements that reference a
                        pay-period start and recomputes <span className="font-mono">week_start</span> on
                        every timesheet entry. Idempotent - periods already starting on Sunday are skipped.
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={migratePayrollToSundayWeek}
                      disabled={migratingPayroll}
                      data-testid="payroll-sun-migration-btn"
                    >
                      <Wrench className="h-3.5 w-3.5 mr-1.5" />
                      {migratingPayroll ? 'Migrating…' : 'Run Migration'}
                    </Button>
                    {formatRun('payroll_sun_sat_migration') && (
                      <p className="text-xs text-muted-foreground" data-testid="last-run-payroll_sun_sat_migration">{formatRun('payroll_sun_sat_migration')}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </AccordionContent>
        </AccordionItem>

      </Accordion>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap" data-testid="system-logs-filters">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Filter className="h-3.5 w-3.5" /> Filters
        </div>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-[180px] h-8 text-xs" data-testid="filter-source">
            <SelectValue placeholder="All Sources" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            {Object.entries(SOURCE_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={critFilter} onValueChange={setCritFilter}>
          <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="filter-criticality">
            <SelectValue placeholder="All Levels" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Levels</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant={showDismissed ? "secondary" : "outline"}
          size="sm"
          className="h-8 text-xs"
          onClick={() => setShowDismissed(!showDismissed)}
          data-testid="toggle-dismissed"
        >
          {showDismissed ? 'Showing Dismissed' : 'Show Dismissed'}
        </Button>
      </div>

      {/* Error List */}
      {loading ? (
        <div className="animate-pulse text-muted-foreground text-sm">Loading system logs...</div>
      ) : errors.length === 0 ? (
        <Card className="border-border bg-card shadow-none">
          <CardContent className="py-12 text-center">
            <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-3" />
            <p className="text-sm font-medium text-muted-foreground">No system errors to display</p>
            <p className="text-xs text-muted-foreground mt-1">All side effects are running cleanly</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="system-logs-list">
          {errors.map(err => (
            <Card key={err.id} className={`border-border bg-card shadow-none ${err.dismissed ? 'opacity-50' : ''}`} data-testid={`system-log-${err.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <Badge variant="outline" className={`text-xs ${CRITICALITY_STYLES[err.criticality] || CRITICALITY_STYLES.medium}`}>
                        {err.criticality?.toUpperCase()}
                      </Badge>
                      <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/30">
                        {SOURCE_LABELS[err.source] || err.source}
                      </Badge>
                      <span className="text-xs text-muted-foreground">{formatTimestamp(err.created_at)}</span>
                    </div>
                    <p className="text-xs font-medium text-foreground mb-1">
                      Step: <span className="text-muted-foreground font-normal">{err.step}</span>
                    </p>
                    <p className="text-xs text-muted-foreground font-mono break-all bg-secondary/30 rounded px-2 py-1.5">
                      {err.message}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span>Triggered by: <span className="text-foreground">{err.user_name || 'System'}</span></span>
                      {err.entity_id && <span>Record: <span className="text-foreground font-mono">{err.entity_id.slice(0, 8)}...</span></span>}
                    </div>
                  </div>
                  {!err.dismissed && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0"
                      onClick={() => dismissError(err.id)}
                      data-testid={`dismiss-${err.id}`}
                    >
                      <XCircle className="h-3.5 w-3.5 text-muted-foreground" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
